import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, FolderKanban, Calendar, User, Users, FolderOpen, FolderCheck, AlertTriangle, Eye } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import { t } from '@/i18n/ro';
import { apiGet } from '@/services/api';
import { canCreateProject } from '@/services/projects.service';
import {
  Button,
  Card,
  LoadingState,
  EmptyState,
  Badge,
} from '@/components/common';
import type { Project, HalCollection } from '@/types';

export type ProjectFilter = 'coordinated' | 'involved' | 'finished';

interface ProjectsFilteredPageProps {
  filter: ProjectFilter;
}

const filterConfig: Record<ProjectFilter, {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  emptyMessage: string;
  endpoint: string;
}> = {
  'coordinated': {
    title: 'Proiecte coordonate',
    description: 'Proiectele în care sunteți Administrator de proiect',
    icon: <Users className="w-6 h-6" />,
    color: 'bg-purple-500',
    emptyMessage: 'Nu coordonați niciun proiect.',
    endpoint: '/auth/data/projects/coordinated',
  },
  'involved': {
    title: 'Proiecte în lucru',
    description: 'Proiectele în care sunteți implicat, dar nu sunteți Administrator',
    icon: <FolderOpen className="w-6 h-6" />,
    color: 'bg-blue-500',
    emptyMessage: 'Nu sunteți implicat în niciun proiect.',
    endpoint: '/auth/data/projects/involved',
  },
  'finished': {
    title: 'Proiecte finalizate',
    description: 'Proiectele care au fost închise',
    icon: <FolderCheck className="w-6 h-6" />,
    color: 'bg-green-500',
    emptyMessage: 'Nu există proiecte finalizate.',
    endpoint: '/auth/data/projects/finished',
  },
};

export function ProjectsFilteredPage({ filter }: ProjectsFilteredPageProps) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canCreate, setCanCreate] = useState(false);

  const config = filterConfig[filter];

  const loadProjects = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [projectsResponse, createPermission] = await Promise.all([
        apiGet<HalCollection<Project>>(config.endpoint),
        canCreateProject(),
      ]);

      setProjects(projectsResponse._embedded?.elements || []);
      setCanCreate(createPermission);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [filter]);

  const handleProjectClick = (project: Project) => {
    navigate(`/projects/${project.id}`);
  };

  if (isLoading) {
    return <LoadingState message={t('common.loading')} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-lg ${config.color} text-white`}>
            {config.icon}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {config.title}
            </h1>
            <p className="text-gray-600 mt-1">
              {config.description}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full font-medium self-center">
            {projects.length} {projects.length === 1 ? 'proiect' : 'proiecte'}
          </span>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={loadProjects}
          >
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* Projects grid */}
      {projects.length === 0 ? (
        <EmptyState
          icon="folder"
          title={config.emptyMessage}
          description="Încercați să reveniți mai târziu sau verificați alte categorii."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => handleProjectClick(project)}
            />
          ))}
        </div>
      )}

    </div>
  );
}

// Componenta pentru cardul de proiect
interface ProjectCardProps {
  project: Project;
  onClick: () => void;
}

function ProjectCard({ project, onClick }: ProjectCardProps) {
  const statusName = project._links.status?.title || '';

  return (
    <Card
      hover
      onClick={onClick}
      className={`group ${project.is_urgent ? 'border-l-4 border-danger-500 bg-danger-50/40' : ''}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
          project.is_urgent ? 'bg-danger-100' : 'bg-primary-100'
        }`}>
          <FolderKanban className={`w-6 h-6 ${project.is_urgent ? 'text-danger-600' : 'text-primary-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 group-hover:text-primary-700 transition-colors truncate">
              {project.name}
            </h3>
            {project.is_following && (
              <Eye className="w-4 h-4 text-primary-500 flex-shrink-0" aria-label="Urmărit" />
            )}
          </div>
          {project.is_urgent && (
            <span
              className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-bold bg-danger-100 text-danger-700 border border-danger-200"
              title="Proiect marcat ca urgent"
            >
              <AlertTriangle className="w-3 h-3" />
              Urgent
            </span>
          )}
          {project.description?.raw && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
              {project.description.raw}
            </p>
          )}
        </div>
      </div>

      {/* Status și info */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {statusName && (
          <Badge variant="info" size="sm">
            {statusName}
          </Badge>
        )}
        {project.public && (
          <Badge variant="default" size="sm">
            Public
          </Badge>
        )}
      </div>

      {/* Meta info */}
      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-500">
        {project.createdAt && (
          <div className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            <span>
              Creat: {format(parseISO(project.createdAt), 'dd MMM yyyy', { locale: ro })}
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}
